import { useGrowthBook } from '@growthbook/growthbook-react';
import type { QueryClient } from '@tanstack/react-query';
import {
  type ParsedLocation,
  RouterProvider,
  createRouter,
} from '@tanstack/react-router';
import type { SignupSearchParams } from 'router/search/signup';
import { isModuleNotFoundError } from 'router/utils';

import { PageSkeleton } from 'components/templates/PageSkeleton';
import { useSignupSteps } from 'hooks/useSignupSteps';
import { DefaultError } from 'pages/DefaultError';
import { NotFoundError } from 'pages/NotFoundError';
import { makeDefaultViewTransitionTypes } from 'router/viewTransitions';
import { useEventTracking } from 'providers/EventTrackingProvider';
import { useFlags } from 'providers/FlagsProvider';
import { useLocalization } from 'providers/LocalizationProvider';
import { useSnackbar } from 'providers/SnackbarProvider';
import { type RumService, rumService } from 'services/rum';
import { isDOMAvailable } from 'utils/browser';

import { routeTree } from '../../routeTree.gen';

export type AppRouterContext = {
  queryClient: QueryClient;
  flags: ReturnType<typeof useFlags>;
  steps: ReturnType<typeof useSignupSteps>;
  snackbarContext: ReturnType<typeof useSnackbar>;
  localization: ReturnType<typeof useLocalization>;
  eventTracking: ReturnType<typeof useEventTracking>;
  rum: RumService;
  growthbook: ReturnType<typeof useGrowthBook>;
};

const buildRouter = () => {
  const newRouter = createRouter({
    routeTree,
    context: {
      queryClient: undefined!,
      flags: undefined!,
      steps: undefined!,
      snackbarContext: undefined!,
      localization: undefined!,
      eventTracking: undefined!,
      rum: undefined!,
      growthbook: undefined!,
    },
    defaultPreload: 'intent',
    // Since we're using React Query, we don't want loader calls to ever be stale
    // This will ensure that the loader is always called when the route is preloaded or visited
    defaultPreloadStaleTime: 0,
    defaultPendingComponent: PageSkeleton,
    defaultErrorComponent: errorObject => {
      const { error } = errorObject;
      if (isModuleNotFoundError(error)) {
        if (
          error instanceof Error &&
          isDOMAvailable() &&
          typeof sessionStorage !== 'undefined'
        ) {
          // Again, we want to reload one time on module not found error and not enter
          // a reload loop if there is some other issue besides an old deploy.
          // That's why we store our reload attempt in sessionStorage.
          // Use error.message as key because it contains the module path that failed.
          const storageKey = `tanstack_router_reload:${error.message}`;
          if (!sessionStorage.getItem(storageKey)) {
            sessionStorage.setItem(storageKey, '1');
            // If it was a module loading error,
            // throw eternal suspense while we wait for window to reload
            window.location.reload();
            return <PageSkeleton />;
          }
        }
      }

      return <DefaultError {...errorObject} />;
    },
    defaultOnCatch: (error, errorInfo) => {
      const match = newRouter?.state?.matches?.at(-1);
      rumService.addError(error, {
        type: 'unhandledException',
        routeId: match?.routeId,
        routePath: match?.pathname,
        componentStack: errorInfo?.componentStack,
      });
    },
    defaultNotFoundComponent: NotFoundError,
    scrollRestoration: true,
    getScrollRestorationKey: location => location.pathname,
    defaultViewTransition: {
      types: makeDefaultViewTransitionTypes,
    },
  });

  return newRouter;
};

let router = buildRouter();

/**
 * Replaces the module-scope router with a fresh one, so no route match cached
 * by a previous render is reused. Only meant for integration tests, which
 * render the App many times in one page. Never call it while the App is
 * mounted: the mounted App keeps the old router until it renders again.
 */
export function resetRouter() {
  router = buildRouter();
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    oppId?: string;
    previousLocation?: ParsedLocation<SignupSearchParams>;
    shouldShowRecoveryLeadModal?: boolean;
  }
}

type AppRouterProps = Readonly<{
  queryClient: AppRouterContext['queryClient'];
  rum: AppRouterContext['rum'];
}>;

export function AppRouter({ queryClient, rum }: AppRouterProps) {
  const flags = useFlags();
  const steps = useSignupSteps();
  const snackbarContext = useSnackbar();
  const localization = useLocalization();
  const eventTracking = useEventTracking();
  const growthbook = useGrowthBook();

  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
        flags,
        steps,
        snackbarContext,
        localization,
        eventTracking,
        rum,
        growthbook,
      }}
    />
  );
}
